/**
 * webmcp-setup.ts — build the root-level CogoToolRegistry for Phase B.
 *
 * The starter's root tree doesn't own an AppV2 runtime (that lives inside
 * the template iframe / <AppV2Lazy>). Yet the agent PROBE still needs a
 * registry populated with the pilot's tool surface so the user can open
 * the drawer on the gallery page, send a message, and watch the
 * confirmation + dispatch loop work end-to-end.
 *
 * Strategy: load the static ontology-manifest.json, register each entry
 * as a tool whose handler returns a structured "proposal acknowledged"
 * payload. When a real runtime attaches later (AppV2's onRuntimeReady),
 * it swaps in its own registry — see packages/cogo-webmcp-runtime/src/
 * renderer/renderer-plugin.ts.
 */

import {
  CogoToolRegistry,
  type ToolManifestEntry,
  type WebMCPTool,
} from "@cogo/webmcp-runtime/react"

import ontologyManifest from "../../public/ontology-manifest.json"

export const STARTER_ONTOLOGY = ontologyManifest as {
  version?: string
  persona?: string
  narrativeContext?: string
  tools: ToolManifestEntry[]
  states?: Array<{ id: string; key: string; blockName: string; scope: string }>
  actions?: Array<{ id: string; flowName: string; blockName: string }>
}

/**
 * Create + populate a CogoToolRegistry from the pilot manifest.
 *
 * Each tool handler is a stub that records the call and returns a
 * structured ack. When the AppV2 runtime connects later it can
 * unregister these and install real handlers.
 */
export function createStarterRegistry(): CogoToolRegistry {
  const registry = new CogoToolRegistry()
  for (const entry of STARTER_ONTOLOGY.tools) {
    const tool: WebMCPTool = {
      name: entry.name,
      description: entry.description,
      inputSchema: entry.inputSchema,
      outputSchema: entry.outputSchema,
      metadata: entry.metadata,
      handler: async (params) => ({
        // Phase B stub. Signals to callers that the agent wired up
        // correctly even though no real app runtime is attached yet.
        ok: true,
        stub: true,
        tool: entry.name,
        blockName: entry.metadata?.blockName ?? null,
        receivedArgs: params,
        message: `Stub handler for "${entry.name}" — attach the AppV2 runtime to swap in the real flow invocation.`,
      }),
    }
    registry.register(tool)
  }
  return registry
}
